// offer 谈判模式「新增权益」弹窗：多选下拉展示未开启的可选权益分组，确认后回调选中的 group code 列表。
// 命令式打开（withFunctionCall + Modal.show），镜像 AssignBusinessModal 范式。
import React, { useMemo, useState } from 'react';
import { Form, Modal } from '@bedrock/components';
import SFormItem from '@mpc/common/src/components/SForm/SFormItem';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import type { BenefitGroupDef } from './config';

/** AddBenefitModal.show() 入参（visible/onCancel 由 withFunctionCall 接管，无需调用方传入） */
export interface AddBenefitModalShowProps {
  /** 未开启的可选权益分组（下拉选项，调用方过滤必填行与已开启行） */
  options?: BenefitGroupDef[];
  /** 确认回调：返回选中的 group code 列表 */
  onOk?: (codes: string[]) => void;
}

interface AddBenefitModalInnerProps extends AddBenefitModalShowProps {
  /** withFunctionCall 注入：右上角 X / 遮罩关闭，同时销毁弹窗 */
  onCancel?: () => void;
  /** withFunctionCall 注入：恒为 true */
  visible?: boolean;
}

const AddBenefitModalInner: React.FC<AddBenefitModalInnerProps> = ({
  options,
  onOk,
  onCancel,
  visible,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // BenefitGroupDef → select options（对齐 AssignBusinessModal 多选下拉范式）
  const selectOptions = useMemo(
    () => (options ?? []).map((g) => ({ name: g.name, value: g.code })),
    [options]
  );

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onOk?.(values?.benefit_codes ?? []);
      onCancel?.();
    } catch (error) {
      // validateFields 校验失败（未选择权益），保持弹窗不关闭
      console.log('add benefit error', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="新增权益"
      visible={visible}
      maskClosable={false}
      okButtonProps={{ loading }}
      cancelButtonProps={{ disabled: loading }}
      closable={!loading}
      width={480}
      onCancel={onCancel}
      onOk={handleOk}
    >
      <Form form={form} layout="vertical">
        <SFormItem
          formItemKey="benefit_codes"
          label="权益"
          required
          componentClass="select"
          elementProps={{
            showSearch: true,
            placeholder: '请选择',
            options: selectOptions,
            multiple: true,
            type: 'label',
          }}
        />
      </Form>
    </Modal>
  );
};

const AddBenefitModalRaw = withFunctionCall(AddBenefitModalInner);

/**
 * 命令式打开「新增权益」弹窗，解耦弹窗与调用方。
 *
 * @example
 * ```ts
 * AddBenefitModal.show({
 *   options: unEnabledOptions,
 *   onOk: (codes) => handleAddBenefits(codes),
 * });
 * ```
 */
export const AddBenefitModal = {
  show(config: AddBenefitModalShowProps) {
    return AddBenefitModalRaw.show(config);
  },
};

export default AddBenefitModal;
